import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { leadsService, Lead, CreateLeadRequest, UpdateLeadRequest, LeadFilters } from '../services/leadsService';

const statusOptions = ['new', 'contacted', 'qualified', 'proposal', 'negotiation', 'closed-won', 'closed-lost'];
const sourceOptions = ['website-cta', 'linkedin', 'referral', 'cold-call', 'email-campaign', 'trade-show', 'other'];

const LeadsPage: React.FC = () => {
  const theme = useTheme();
  
  // State
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sourceFilter, setSourceFilter] = useState<string>('');
  const [totalLeads, setTotalLeads] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [leadToDelete, setLeadToDelete] = useState<Lead | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<CreateLeadRequest>({
    name: '',
    email: '',
    phone: '',
    company: '',
    source: 'website-cta',
    status: 'new',
    service_interest: '',
    mrr_range: '',
    tech_expenses: '',
    software_interaction: '',
    message: '',
    notes: '',
  });
  
  // Notification state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Fetch leads
  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const filters: LeadFilters = {
        limit: paginationModel.pageSize,
        offset: paginationModel.page * paginationModel.pageSize,
        search: searchText || undefined,
        status: statusFilter || undefined,
        source: sourceFilter || undefined,
      };
      const response = await leadsService.getLeads(filters);
      setLeads(response.leads);
      setTotalLeads(response.total);
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      setSnackbar({ open: true, message: 'Failed to load leads', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [paginationModel, searchText, statusFilter, sourceFilter]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (paginationModel.page !== 0) {
        setPaginationModel({ ...paginationModel, page: 0 });
      } else {
        fetchLeads();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Handle form input changes
  const handleFormChange = (field: keyof CreateLeadRequest) => (
    event: React.ChangeEvent<HTMLInputElement | { value: unknown }>
  ) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  // Open dialog for new lead
  const handleAddLead = () => {
    setEditingLead(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      company: '',
      source: 'website-cta',
      status: 'new',
      service_interest: '',
      mrr_range: '',
      tech_expenses: '',
      software_interaction: '',
      message: '',
      notes: '',
    });
    setDialogOpen(true);
  };

  // Open dialog for editing
  const handleEditLead = (lead: Lead) => {
    setEditingLead(lead);
    setFormData({
      name: lead.name,
      email: lead.email,
      phone: lead.phone || '',
      company: lead.company || '',
      source: lead.source,
      status: lead.status,
      service_interest: lead.service_interest || '',
      mrr_range: lead.mrr_range || '',
      tech_expenses: lead.tech_expenses || '',
      software_interaction: lead.software_interaction || '',
      message: lead.message || '',
      notes: lead.notes || '',
    });
    setDialogOpen(true);
  };

  // Save lead (create or update)
  const handleSaveLead = async () => {
    try {
      if (editingLead) {
        await leadsService.updateLead(editingLead.id, formData as UpdateLeadRequest);
        setSnackbar({ open: true, message: 'Lead updated successfully', severity: 'success' });
      } else {
        await leadsService.createLead(formData);
        setSnackbar({ open: true, message: 'Lead created successfully', severity: 'success' });
      }
      setDialogOpen(false);
      fetchLeads();
    } catch (error) {
      console.error('Failed to save lead:', error);
      setSnackbar({ open: true, message: 'Failed to save lead', severity: 'error' });
    }
  };

  // Delete lead
  const handleDeleteClick = (lead: Lead) => {
    setLeadToDelete(lead);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!leadToDelete) return;
    try {
      await leadsService.deleteLead(leadToDelete.id);
      setSnackbar({ open: true, message: 'Lead deleted successfully', severity: 'success' });
      setDeleteDialogOpen(false);
      setLeadToDelete(null);
      fetchLeads();
    } catch (error) {
      console.error('Failed to delete lead:', error);
      setSnackbar({ open: true, message: 'Failed to delete lead', severity: 'error' });
    }
  };

  // Format status for display
  const formatStatus = (status: string) => {
    return status.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get status color
  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'new': theme.palette.info.main,
      'contacted': theme.palette.warning.main,
      'qualified': theme.palette.success.light,
      'proposal': theme.palette.secondary.main,
      'negotiation': theme.palette.warning.dark,
      'closed-won': theme.palette.success.main,
      'closed-lost': theme.palette.error.main,
    };
    return colors[status] || theme.palette.grey[500];
  };

  // Columns definition
  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: 'company',
      headerName: 'Company',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          <Typography variant="body2">{params.value || '-'}</Typography>
        </Box>
      ),
    },
    {
      field: 'contact',
      headerName: 'Contact',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          {params.row.email && (
            <Tooltip title={params.row.email}>
              <IconButton
                size="small"
                sx={{ color: 'primary.main' }}
                onClick={() => window.location.href = `mailto:${params.row.email}`}
              >
                <EmailIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {params.row.phone && (
            <Tooltip title={params.row.phone}>
              <IconButton
                size="small"
                sx={{ color: 'primary.main' }}
                onClick={() => window.location.href = `tel:${params.row.phone}`}
              >
                <PhoneIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params: GridRenderCellParams) => {
        const color = getStatusColor(params.value);
        return (
          <Chip
            label={formatStatus(params.value)}
            size="small"
            sx={{
              bgcolor: alpha(color, 0.15),
              color: color,
              border: `1px solid ${color}`,
              fontWeight: 500,
            }}
          />
        );
      },
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 130,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
          {params.value?.replace(/-/g, ' ') || '-'}
        </Typography>
      ),
    },
    {
      field: 'service_interest',
      headerName: 'Interest',
      width: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2" noWrap>
          {params.value || '-'}
        </Typography>
      ),
    },
    {
      field: 'created_at',
      headerName: 'Created',
      width: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Typography variant="body2">
          {params.value ? new Date(params.value).toLocaleDateString() : '-'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              sx={{ color: 'text.secondary' }}
              onClick={() => handleEditLead(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              sx={{ color: 'error.main' }}
              onClick={() => handleDeleteClick(params.row)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Leads
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {totalLeads} total leads
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchLeads}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddLead}
            sx={{
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            Add Lead
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper
        sx={{
          p: 2,
          mb: 3,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              variant="outlined"
              size="small"
              placeholder="Search leads..."
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {formatStatus(status)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Source Filter</InputLabel>
              <Select
                value={sourceFilter}
                label="Source Filter"
                onChange={(e) => setSourceFilter(e.target.value)}
              >
                <MenuItem value="">All Sources</MenuItem>
                {sourceOptions.map((source) => (
                  <MenuItem key={source} value={source}>
                    {source.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<FilterIcon />}
              onClick={() => {
                setSearchText('');
                setStatusFilter('');
                setSourceFilter('');
              }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Data Grid */}
      <Paper
        sx={{
          height: 600,
          width: '100%',
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          '& .MuiDataGrid-root': {
            border: 'none',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: 'text.primary',
          },
          '& .MuiDataGrid-columnHeaders': {
            bgcolor: 'background.default',
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: 'text.primary',
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.default',
            color: 'text.primary',
          },
          '& .MuiTablePagination-root': {
            color: 'text.primary',
          },
        }}
      >
        <DataGrid
          rows={leads}
          columns={columns}
          loading={loading}
          rowCount={totalLeads}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={(newSelection) => setSelectedRows(newSelection)}
          rowSelectionModel={selectedRows}
          slots={{
            loadingOverlay: () => (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ),
          }}
        />
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingLead ? 'Edit Lead' : 'Add New Lead'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={handleFormChange('name')}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleFormChange('email')}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={handleFormChange('phone')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Company"
                value={formData.company}
                onChange={handleFormChange('company')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Status"
                value={formData.status}
                onChange={handleFormChange('status')}
              >
                {statusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {formatStatus(status)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Source"
                value={formData.source}
                onChange={handleFormChange('source')}
              >
                {sourceOptions.map((source) => (
                  <MenuItem key={source} value={source}>
                    {source.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Service Interest"
                value={formData.service_interest}
                onChange={handleFormChange('service_interest')}
                placeholder="e.g., Web Development, Marketing"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="MRR Range"
                value={formData.mrr_range}
                onChange={handleFormChange('mrr_range')}
                placeholder="e.g., $1,000-$5,000"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tech Expenses"
                value={formData.tech_expenses}
                onChange={handleFormChange('tech_expenses')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Software Interaction"
                value={formData.software_interaction}
                onChange={handleFormChange('software_interaction')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message"
                value={formData.message}
                onChange={handleFormChange('message')}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                value={formData.notes}
                onChange={handleFormChange('notes')}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveLead}
            disabled={!formData.name || !formData.email}
          >
            {editingLead ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the lead "{leadToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeadsPage;